/* eslint-disable react/prop-types */
/*
Author: Ayi Regiarty
*/

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb, isWebPlatform } from '@global/screens';
import { View, StyleSheet, Dimensions } from 'react-native';

import {
  setTitleHeader,
  setDefaultStackOption,
  hasNoAccess,
  getHeaderTitleStyle,
} from '@global/helpers';

import HamburgerButton from '@components/navbar/HamburgerButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import BackButton from '@components/navbar/BackButton';
import MoreButton from '@components/navbar/MoreButton';
import CommentHeaderButton from '@components/comment/CommentHeaderButton';

import LPBScreen from '@/screens/LPB/LPBScreen';
import LPBDetailScreen from '@/screens/LPB/LPBDetailScreen';
import LPBFormScreen from '@/screens/LPB/LPBFormScreen';

import { showPopup } from '@/store/modal';
import { NET_DOMAIN } from '@/constants';

const Stack = createStackNavigator();

const LPBNavigator = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const tempData = useSelector((state) => state.global.tempData);
  const userData = useSelector((state) => state.auth.user);

  const isShowNotification =
    ['3pl'].includes(userData?.role?.code) ||
    (isWebPlatform ? NET_DOMAIN.includes(window.location.hostname) : false);
  // Same audience: 3PL users and the external (NET) web domain get no comments.
  const hideComments = isShowNotification;

  return (
    <Stack.Navigator
      initialRouteName="LPBScreen"
      screenOptions={(props) => ({
        headerLeft: () => <HamburgerButton {...props} />,
        headerRight: () => <NotificationSettingButton {...props} />,
        ...setDefaultStackOption(insets, CardStyleInterpolators, true),
      })}>
      <Stack.Screen
        name="LPBScreen"
        component={LPBScreen}
        options={(props) => {
          let isBack = false;
          let title = 'Goods Received Note';
          if (props?.route?.params?.titleScreen) {
            isBack = true;
            title = `${title} ${
              !props?.route?.params?.isHome
                ? props.route.params.titleScreen
                : ''
            }`;
          }
          const showFilter = () => {
            dispatch(showPopup({ name: 'showFilter', value: 'lpb' }));
          };
          const showExport = () => {
            dispatch(showPopup({ name: 'showLPBExport', value: true }));
          };
          const goBack = () => {
            props.navigation.navigate('HomeNavigator');
          };

          const menus = [
            { type: 'function', name: 'Filter', callback: showFilter },
            { type: 'function', name: 'Export', callback: showExport },
          ];

          return {
            title: setTitleHeader(title),
            headerLeft: () =>
              isBack ? (
                <BackButton {...props} onPress={() => goBack()} />
              ) : (
                <HamburgerButton {...props} />
              ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <View
                  // eslint-disable-next-line react-native/no-inline-styles
                  style={{
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}>
                  {isShowNotification ? (
                    <NotificationSettingButton {...props} />
                  ) : (
                    false
                  )}
                  <MoreButton menu={menus} {...props} />
                </View>
              ),
          };
        }}
      />
      <Stack.Screen
        name="LPBFormScreen"
        component={LPBFormScreen}
        options={(props) => {
          const route = props.route;
          let title = 'Add Goods Received Note';
          if (route.params && route.params.titleScreen) {
            title = 'Edit Goods Received Note';
          }
          const goBack = () => {
            if (route.params && route.params.titleScreen) {
              props.navigation.navigate('LPBDetailScreen', route.params);
            } else {
              props.navigation.navigate('HomeNavigator');
            }
          };
          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton {...props} onPress={() => goBack()} />
            ),
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : false,
          };
        }}
      />
      <Stack.Screen
        name="LPBDetailScreen"
        component={LPBDetailScreen}
        options={(props) => {
          const hasAccess = !hasNoAccess([
            'superadmin',
            '3pl',
            'spv_logistic',
            'general',
          ]);
          const route = props.route;
          let title = 'Goods Received Note Detail';
          if (route?.params?.titleScreen) {
            title = route.params.titleScreen;
          }

          const menus = [];
          if (hasAccess && ['draft', 'rejected'].includes(tempData)) {
            menus.push({
              type: 'redirect',
              name: 'Edit',
              screen: 'LPBFormScreen',
              params: route.params,
            });
          }
          const goBack = () => {
            props.navigation.navigate('LPBScreen');
          };
          const showCommentButton = !isWeb && route.params?.id && !hideComments;
          return {
            title: setTitleHeader(title),
            // [B2B-5891][B2B-5924] With the Comment button, headerRight holds TWO buttons
            // (Comment + More, ~50px each), but the default title width assumes one, so a
            // long title overlaps the Comment button on narrow screens. Size the title to the
            // space left in the row: Back 50 + title margins 16/16 + Comment 50 + More 50 = 182px.
            // Only set when needed so the stack's default title style stays in place otherwise.
            ...(showCommentButton
              ? {
                  headerTitleStyle: getHeaderTitleStyle(
                    Dimensions.get('window').width - 182,
                  ),
                }
              : {}),
            headerLeft: () => (
              <BackButton
                {...props}
                notUseLastRoute={true}
                onPress={() => goBack()}
              />
            ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <View style={styles.headerRightRow}>
                  {showCommentButton ? (
                    <CommentHeaderButton
                      onPress={() =>
                        props.navigation.setParams({ showComments: true })
                      }
                      drawerVisible={Boolean(route.params?.showComments)}
                      entityType="lpb"
                      entityId={route.params.id}
                    />
                  ) : (
                    false
                  )}
                  {menus.length > 0 ? (
                    <MoreButton menu={menus} {...props} />
                  ) : null}
                </View>
              ),
          };
        }}
      />
    </Stack.Navigator>
  );
};

const styles = StyleSheet.create({
  headerRightRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default LPBNavigator;
